/**
 * Referral Service
 * Handles referral code generation, application, and rewards processing
 */

import { supabase } from '../config/supabase';

class ReferralService {
  constructor() {
    this.backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
  }

  /**
   * Generate a unique referral code
   */
  generateReferralCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Get user's referral code and create one if it doesn't exist
   */
  async getMyReferralCode() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      // First try to get existing code
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('referral_code')
        .eq('id', session.user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      if (profile?.referral_code) {
        return { success: true, referralCode: profile.referral_code };
      }

      // Generate new code if none exists
      let attempts = 0;
      let newCode;
      
      while (attempts < 10) {
        newCode = this.generateReferralCode();
        
        // Check if code already exists
        const { data: existing } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('referral_code', newCode)
          .limit(1);

        if (!existing || existing.length === 0) {
          break;
        }
        attempts++;
      }

      if (attempts >= 10) {
        throw new Error('Failed to generate unique referral code');
      }

      // Update user profile with new code
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ referral_code: newCode })
        .eq('id', session.user.id);

      if (updateError) {
        throw updateError;
      }

      return { success: true, referralCode: newCode };
    } catch (error) {
      console.error('Error getting referral code:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Apply a referral code for the current user
   */
  async applyReferralCode(code) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${this.backendUrl}/api/referrals/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ referralCode: code })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to apply referral code');
      }

      return { success: true, ...result };
    } catch (error) {
      console.error('Error applying referral code:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Validate a referral code
   */
  async validateReferralCode(code) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${this.backendUrl}/api/referrals/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ referralCode: code })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to validate referral code');
      }

      return { success: true, valid: result.valid };
    } catch (error) {
      console.error('Error validating referral code:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get referral statistics for the current user
   */
  async getReferralStats() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${this.backendUrl}/api/referrals/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to get referral stats');
      }

      return { success: true, ...result };
    } catch (error) {
      console.error('Error getting referral stats:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get referral leaderboard
   */
  async getReferralLeaderboard() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${this.backendUrl}/api/referrals/leaderboard`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to get leaderboard');
      }

      return { success: true, leaderboard: result.leaderboard };
    } catch (error) {
      console.error('Error getting referral leaderboard:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's referrals list
   */
  async getReferralsList() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${this.backendUrl}/api/referrals/my-referrals`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to get referrals list');
      }

      return { success: true, referrals: result.referrals || [] };
    } catch (error) {
      console.error('Error getting referrals list:', error);
      return { success: false, error: error.message, referrals: [] };
    }
  }

  /**
   * Generate share URL for referral
   */
  generateShareUrl(referralCode) {
    const baseUrl = window.location.origin;
    return `${baseUrl}/signup?ref=${referralCode}`;
  }

  /**
   * Share referral via Web Share API or fallback to clipboard
   */
  async shareReferral(referralCode) {
    const shareUrl = this.generateShareUrl(referralCode);
    const shareText = `Join me on Sanity Reps - your AI wellness companion! Use my referral code ${referralCode} and we both get rewards. Sign up here: ${shareUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Sanity Reps',
          text: shareText,
          url: shareUrl
        });
        return { success: true, method: 'native' };
      } catch (error) {
        // User cancelled or error occurred, fallback to clipboard
      }
    }

    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(shareText);
      return { success: true, method: 'clipboard' };
    } catch (error) {
      return { success: false, error: 'Failed to share or copy to clipboard' };
    }
  }
}

export const referralService = new ReferralService();
export default referralService;
